import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Platform } from '@ionic/angular';
import { AndroidIntentService } from './android-intent.service';
import { IosIntentService } from './ios-intent.service';
import { LogCollectorService } from './log-collector.service';

@Injectable({
  providedIn: 'root'
})
export class IntentHandlerService {
  private sharedData: any = null;

  constructor(
    private platform: Platform,
    private androidIntentService: AndroidIntentService,
    private iosIntentService: IosIntentService,
    private logCollector: LogCollectorService
  ) {
    this.initializeIntentHandling();
  }

  private async initializeIntentHandling() {
    if (this.platform.is('capacitor')) {
      this.logCollector.log('IntentHandler', 'Initializing intent handling');

      // Listen for app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.logCollector.log('IntentHandler', 'App became active, checking for intent');
          this.handleIncomingIntent();
        }
      });

      // Listen for app resume
      App.addListener('resume', () => {
        this.logCollector.log('IntentHandler', 'App resumed, checking for intent');
        this.handleIncomingIntent();
      });

      // Listen for androidShare events directly
      if (this.platform.is('android')) {
        window.addEventListener('androidShare', () => {
          this.logCollector.log('IntentHandler', 'Received androidShare event, checking for shared content');
          this.handleIncomingIntent();
        });
      }

      // ✅ CRITICAL: Check for intent data on app start (with delay to ensure services are ready)
      // This handles the case when app is opened from a closed state via share
      this.logCollector.log('IntentHandler', 'Scheduling initial intent check');
      setTimeout(() => {
        this.logCollector.log('IntentHandler', 'Performing initial intent check on app start (500ms)');
        this.handleIncomingIntent();
      }, 500);

      // ✅ Also check again after a longer delay to catch delayed share events
      setTimeout(() => {
        this.logCollector.log('IntentHandler', 'Performing delayed intent check (2000ms)');
        this.handleIncomingIntent();
      }, 2000);
    }
  }

  private async handleIncomingIntent() {
    try {
      this.logCollector.log('IntentHandler', 'handleIncomingIntent called', {
        isAndroid: this.platform.is('android'),
        isIOS: this.platform.is('ios')
      });

      // Check for platform-specific intent data
      if (this.platform.is('android')) {
        this.logCollector.log('IntentHandler', 'Checking Android shared content');
        const sharedContent = await this.androidIntentService.checkForSharedContent();
        this.logCollector.log('IntentHandler', 'Android shared content result', sharedContent);
        if (sharedContent) {
          this.logCollector.log('IntentHandler', 'Processing Android shared data');
          this.processSharedData(sharedContent);
          return;
        }
      } else if (this.platform.is('ios')) {
        this.logCollector.log('IntentHandler', 'Checking iOS shared content');
        const sharedContent = await this.iosIntentService.checkForSharedContent();
        this.logCollector.log('IntentHandler', 'iOS shared content result', sharedContent);
        if (sharedContent) {
          this.logCollector.log('IntentHandler', 'Processing iOS shared data');
          this.processSharedData(sharedContent);
          return;
        }
      }

      // Fallback to other methods
      this.logCollector.log('IntentHandler', 'Trying fallback methods');
      await this.checkForSharedText();
      await this.checkForSharedFiles();
    } catch (error) {
      this.logCollector.log('IntentHandler', 'Error handling intent', error, 'error');
    }
  }

  private async checkForSharedText() {
    // Check if there's text in the URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sharedText = urlParams.get('text') || urlParams.get('shared_text');
    
    if (sharedText) {
      this.processSharedData({
        type: 'text',
        content: sharedText
      });
    }
  }

  private async checkForSharedFiles() {
    // Check for file sharing
    const urlParams = new URLSearchParams(window.location.search);
    const sharedFile = urlParams.get('file') || urlParams.get('shared_file');
    
    if (sharedFile) {
      this.processSharedData({
        type: 'file',
        content: sharedFile
      });
    }
  }

  private processSharedData(data: any) {
    this.logCollector.log('IntentHandler', 'processSharedData called', data);
    this.sharedData = data;

    // Emit event or handle the shared data
    this.logCollector.log('IntentHandler', 'Dispatching sharedDataReceived event');
    window.dispatchEvent(new CustomEvent('sharedDataReceived', {
      detail: { data: this.sharedData }
    }));
    this.logCollector.log('IntentHandler', 'Event dispatched successfully');
  }

  public getSharedData(): any {
    return this.sharedData;
  }

  public clearSharedData(): void {
    this.sharedData = null;
  }

  // Method to handle Android intent extras
  public handleAndroidIntent(extras: any) {
    console.log('Android intent extras:', extras);
    
    if (extras && extras['android.intent.extra.TEXT']) {
      this.processSharedData({
        type: 'text',
        content: extras['android.intent.extra.TEXT']
      });
    }
    
    if (extras && extras['android.intent.extra.STREAM']) {
      this.processSharedData({
        type: 'file',
        content: extras['android.intent.extra.STREAM']
      });
    }
  }
}
